// "Continue with Google" — Google Identity Services, AUTHORIZATION CODE flow.
//
// WHY THE CODE FLOW. The previous implementation used the ID-token flow
// (`google.accounts.id.initialize` + `renderButton`). That flow has NO option to ask
// Google for the account chooser: with a session in the browser it returns a credential
// for whichever Google account was used last, so a practitioner signed into a personal
// Gmail could not reach their practice account at all — and, on shared machines, the
// second person silently signed in as the first. The code client is the only GIS web flow
// with that switch:
//
//   google.accounts.oauth2.initCodeClient({ …, select_account: true })
//   → "Boolean value to prompt the user to select an account"
//     https://developers.google.com/identity/oauth2/web/reference/js-reference#CodeClientConfig
//
// It hands the page a one-time authorization CODE instead of an ID token; the code goes to
// POST /api/auth/session { code }, which exchanges it server-side (the client secret never
// touches the browser) and mints the same session cookie the ID-token path did.
//
// Consequence for the UI: Google's rendered <iframe> button belongs to the ID-token flow
// and is gone with it. The button below is ours — a white pill with the Google mark, which
// is what `renderButton({ theme: "outline", shape: "pill" })` looked like, so it reads the
// same on the light admin card and on the dark SaaS signup.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useT } from "@/lib/LanguageContext";
import { lookup } from "@/lib/i18n";
import { readStoredLang } from "./storedLang";

const GIS_SRC = "https://accounts.google.com/gsi/client";
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
// Identity only. The Calendar connection asks for its own scopes through its own consent
// (backend/calendar_sync_routes.py) — signing in must never carry them.
const SCOPE = "openid email profile";

// Reads the persisted language (cf. lib/ErrorBoundary.jsx): loadGisScript lives outside
// any component (module-level shared promise), so there is no useT() here.
const currentLang = readStoredLang;

// Shared promise: the GIS script is loaded once, even when several
// <GoogleLoginButton /> are mounted (successively or in parallel).
let gisScriptPromise = null;
function loadGisScript() {
  if (gisScriptPromise) return gisScriptPromise;
  gisScriptPromise = new Promise((resolve, reject) => {
    if (window.google?.accounts?.oauth2) {
      resolve();
      return;
    }
    const existing = document.querySelector(`script[src="${GIS_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error(lookup("common", "googleAuth.scriptLoadFailed", currentLang()))));
      return;
    }
    const el = document.createElement("script");
    el.src = GIS_SRC;
    el.async = true;
    el.defer = true;
    el.onload = () => resolve();
    el.onerror = () => {
      gisScriptPromise = null; // allows a retry if the load failed
      reject(new Error(lookup("common", "googleAuth.scriptLoadFailed", currentLang())));
    };
    document.body.appendChild(el);
  });
  return gisScriptPromise;
}

// Google's mark, per the "Sign in with Google" branding guidelines.
const GoogleMark = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62z" />
    <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.81.54-1.84.86-3.05.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.34A9 9 0 0 0 9 18z" />
    <path fill="#FBBC05" d="M3.97 10.72a5.41 5.41 0 0 1 0-3.44V4.94H.96a9 9 0 0 0 0 8.12l3.01-2.34z" />
    <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.94l3.01 2.34C4.68 5.16 6.66 3.58 9 3.58z" />
  </svg>
);

/**
 * GoogleLoginButton
 * Props:
 * - onSuccess(user): called once the server session exists (cookie set) and the auth
 *   context has been refreshed. `user` is the body of POST /api/auth/session; callers that
 *   only need "we are in" may ignore it. Each page decides its own post-sign-in navigation.
 * - onError(err): optional. Receives the AXIOS error, response body included, so the caller
 *   can map a stable backend code to its own sentence (e.g. 403 TENANT_SUSPENDED →
 *   admin/Login.jsx's loginErrorKey). GIS-side failures arrive as a plain Error.
 *   PASSING onError TAKES OVER THE MESSAGE: this component then renders none, so the user
 *   never reads two different explanations of one failure.
 * - label: optional override of the button text (already localized by the caller).
 */
export default function GoogleLoginButton({ onSuccess, onError, label }) {
  const { refresh } = useAuth();
  const t = useT("common");
  // The KEY, not the sentence: translating at render keeps the message correct when the
  // visitor switches language after the failure.
  const [errorKey, setErrorKey] = useState(null);
  const [pending, setPending] = useState(false);
  const [ready, setReady] = useState(false);
  const codeClientRef = useRef(null);
  // Refs, not deps: the GIS callbacks are registered once, when the code client is built,
  // and must see the CURRENT handlers when they fire minutes later.
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const liveRef = useRef(true);

  // Only surface a message here when nobody else owns it (see onError above).
  const showError = useCallback((key) => {
    if (!onErrorRef.current) setErrorKey(key);
  }, []);

  useEffect(() => {
    liveRef.current = true;
    if (!CLIENT_ID) {
      setErrorKey("googleAuth.configMissing");
      return undefined;
    }

    // Google hands us the authorization code; the server does the exchange.
    const handleCode = async (response) => {
      if (response?.error || !response?.code) {
        // OAuth-level refusal (access_denied, invalid_scope…): the user is back on the
        // page with nothing to send.
        setPending(false);
        showError("googleAuth.signInFailed");
        onErrorRef.current?.(new Error(response?.error || "missing_code"));
        return;
      }
      try {
        const { data } = await api.post("/auth/session", { code: response.code });
        await refresh();
        if (!liveRef.current) return;
        setPending(false);
        onSuccessRef.current?.(data);
      } catch (e) {
        console.error("[auth] Google sign-in failed", e);
        if (!liveRef.current) return;
        setPending(false);
        showError("googleAuth.signInFailed");
        // The axios error, untouched: `e.response.data.detail` carries the backend's
        // stable refusal code (403 TENANT_SUSPENDED), which only the caller can phrase.
        onErrorRef.current?.(e);
      }
    };

    // Non-OAuth failures (popup blocked, popup closed by the user) — GIS reports these
    // here, never through `callback`.
    const handleNonOAuthError = (err) => {
      setPending(false);
      if (err?.type === "popup_closed") return; // deliberate dismissal, not an error
      showError("googleAuth.popupBlocked");
      onErrorRef.current?.(new Error(err?.type || "popup_error"));
    };

    loadGisScript()
      .then(() => {
        if (!liveRef.current || !window.google?.accounts?.oauth2) return;
        codeClientRef.current = window.google.accounts.oauth2.initCodeClient({
          client_id: CLIENT_ID,
          scope: SCOPE,
          ux_mode: "popup",
          // THE fix: force Google's account chooser on every sign-in instead of
          // silently reusing the browser's last Google account.
          select_account: true,
          // GIS defaults this to TRUE, which makes Google request every scope this OAuth
          // client was ever granted by the account on top of SCOPE. The Calendar connection
          // shares the client, so an account that connected its calendar was re-asked for
          // the sensitive Calendar scope — behind "Google hasn't verified this app" — on
          // every sign-in. Sign-in asks for identity and nothing else.
          include_granted_scopes: false,
          callback: handleCode,
          error_callback: handleNonOAuthError,
        });
        setReady(true);
      })
      .catch((e) => {
        console.error("[auth] GIS script load failed", e);
        if (liveRef.current) setErrorKey("googleAuth.loadFailed");
      });

    return () => {
      liveRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = () => {
    if (!codeClientRef.current || pending) return;
    setErrorKey(null);
    setPending(true);
    codeClientRef.current.requestCode();
  };

  const text = label || t("googleAuth.continueWithGoogle");

  return (
    <div className="w-full flex flex-col items-center">
      <button
        type="button"
        onClick={start}
        disabled={!ready || pending}
        aria-busy={pending}
        data-testid="google-signin-button"
        className="w-full inline-flex items-center justify-center gap-3 px-6 py-3 rounded-full bg-white text-[#1f1f1f] border border-black/15 shadow-sm text-sm font-medium transition hover:bg-black/[0.03] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4285F4] disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <GoogleMark />
        <span>{pending ? t("googleAuth.signingIn") : text}</span>
      </button>
      {errorKey && (
        <p className="mt-2 text-xs text-red-500 text-center" data-testid="google-signin-error">{t(errorKey)}</p>
      )}
    </div>
  );
}
